import { type ReactElement } from 'react'
import { isEmpty, isNotDefined, type DashboardFilter } from '@terros/common'
import { Group, Text } from '@mantine/core'
import {
  getWidgetScope,
  toWidgetScopeRefKey,
  usePrimaryTeam,
  useProfile,
  useTranslation,
  type WidgetScopeRef,
} from '@front/model'
import { RoleInfo, TeamInfo, UserInfo } from '@components'

type Props = {
  /** The widget's effective filter — its own, or the dashboard's. */
  filter: DashboardFilter | undefined
}

/**
 * The entities a widget's data is scoped to, so a card's numbers can be read
 * without opening its config. A single entity shows its avatar and name; several
 * collapse into a facepile with a trailing "+N". A company-wide widget resolves
 * to nothing and renders nothing.
 */
export function WidgetScopeAvatars({ filter }: Props): ReactElement | null {
  const { t } = useTranslation()
  const scope = getWidgetScope(filter)

  if (isEmpty(scope.refs)) return null

  const isSingle = scope.refs.length === 1 && scope.overflow === 0

  return (
    <Group gap={isSingle ? 'xs' : 4} wrap='nowrap'>
      {scope.refs.map((ref) => (
        <ScopeEntity avatarOnly={!isSingle} key={toWidgetScopeRefKey(ref)} scopeRef={ref} />
      ))}
      {scope.overflow > 0 && (
        <Text c='dimmed' className='font-semibold' size='xs'>
          {t('+{{count}}', { count: scope.overflow })}
        </Text>
      )}
    </Group>
  )
}

type EntityProps = {
  scopeRef: WidgetScopeRef
  avatarOnly: boolean
}

/**
 * `currentUser` / `currentTeam` mean "whoever is looking", so they resolve
 * against the viewer rather than naming a fixed entity.
 */
function ScopeEntity({ scopeRef, avatarOnly }: EntityProps): ReactElement | null {
  const { user } = useProfile()
  const primaryTeamId = usePrimaryTeam()
  const avatarProps = { avatarOnly, linked: false, size: 'xs' as const }

  switch (scopeRef.kind) {
    case 'currentUser':
      if (isNotDefined(user)) return null
      return <UserInfo {...avatarProps} userId={user.userId} />
    case 'currentTeam':
      if (isNotDefined(primaryTeamId)) return null
      return <TeamInfo {...avatarProps} teamId={primaryTeamId} />
    case 'user':
      return <UserInfo {...avatarProps} userId={scopeRef.userId} />
    case 'team':
      return <TeamInfo {...avatarProps} teamId={scopeRef.teamId} />
    case 'role':
      return <RoleInfo {...avatarProps} roleId={scopeRef.roleId} />
  }
}
